import { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  IconButton,
  Box,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import StopIcon from "@mui/icons-material/Stop";
import { useQueries } from "@tanstack/react-query";
import { useSidebar } from "contexts/sidebar";
import { useInsightsEnabled } from "hooks/use-insights-enabled";
import Header from "components/header";
import {
  ALL_WORKSPACES_ID,
  getInsightsErrorMessage,
  getWorkspacesForSeller,
  sendInsightsAgentMessage,
  IInsightsPrefabResponse,
} from "v2/services/insights.service";
import { UserMessage } from "./UserMessage";
import { AssistantMessage, IAssistantContent } from "./AssistantMessage";
import { LoadingMessage } from "./LoadingMessage";
import { ChatSuggestions } from "./ChatSuggestions";
import { ReportButtons } from "./ReportButtons";
import { QuickQuestions } from "./QuickQuestions";
import { WorkspaceSelector } from "./WorkspaceSelector";
import {
  ChatContainer,
  MessagesArea,
  InputCard,
  InputToolbar,
  InputDock,
  CenteredColumn,
  EmptyStateContainer,
} from "./ChatMessage.styled";
import { CONDUCCO_PRIMARY, INSIGHTS_FONT } from "./tokens";
import { useAlert } from "contexts/alert";

const INSIGHTS_WORKSPACE_STORAGE_KEY = "@conducco:insightsWorkspaceId";
const INSIGHTS_CHAT_HISTORY_STORAGE_KEY = "@conducco:insightsChatHistory";
const SESSION_MAX_MESSAGES = 5;
const FEATURE_DISABLED_MESSAGE = "Funcionalidade não disponível para esta organização";
// Chips de pergunta pronta acima do input, desligadas a pedido — mantidas no
// código para reativar/reformular depois em vez de recriar do zero.
const QUICK_QUESTIONS_ENABLED = false;

type ChatMessage =
  | { type: "user"; question: string }
  | { type: "assistant"; content: IAssistantContent[] };

interface IWorkspaceChatState {
  messages: ChatMessage[];
  sentCount: number;
  sessionId: string;
}

function loadChatHistoryMap(): Record<string, IWorkspaceChatState> {
  try {
    const raw = sessionStorage.getItem(INSIGHTS_CHAT_HISTORY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function InsightsPage() {
  const [workspaceId, setWorkspaceId] = useState(
    () => localStorage.getItem(INSIGHTS_WORKSPACE_STORAGE_KEY) || ""
  );
  const chatHistoryRef = useRef<Record<string, IWorkspaceChatState> | null>(null);
  if (chatHistoryRef.current === null) {
    chatHistoryRef.current = loadChatHistoryMap();
  }
  const chatHistory = chatHistoryRef.current;
  const didMountRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>(
    () => chatHistory[workspaceId]?.messages ?? []
  );
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sentCount, setSentCount] = useState(
    () => chatHistory[workspaceId]?.sentCount ?? 0
  );
  const [sessionId, setSessionId] = useState(
    () => chatHistory[workspaceId]?.sessionId ?? crypto.randomUUID()
  );
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { openAlert } = useAlert();
  const {
    enabled: flagEnabled,
    qualifyingBuyerIds,
    isLoading: isCheckingFlag,
  } = useInsightsEnabled();

  // Não há endpoint de listagem dedicado ao Insights: cada opção do sidebar é um
  // fornecedor com quem o comprador logado tem relação, e cada um expõe os próprios
  // workspaces via /v2/organizations/:sellerId/workspaces (mesma rota do WorkspaceListContracts).
  const { organizationOptions, isLoading: isLoadingSellers } = useSidebar();

  const workspaceQueries = useQueries({
    queries: organizationOptions.map((seller) => ({
      queryKey: ["insights-workspaces", seller.id],
      queryFn: () => getWorkspacesForSeller(seller.id),
      retry: false,
    })),
  });

  const isLoadingWorkspaces =
    isLoadingSellers || workspaceQueries.some((query) => query.isLoading);
  const allWorkspaces = workspaceQueries.flatMap((query) => query.data ?? []);

  // Um seller enxerga todos os buyers que gerencia, mas so os buyers com
  // conector Snowflake na allowlist tem dado consultavel — os outros levariam
  // o Agent a consultar um CNPJ sem nenhuma linha. `qualifyingBuyerIds` vem do
  // proprio gate de feature flag, entao a listagem fica no mesmo escopo do
  // acesso. Sem a lista (buyer logado, ou backend antigo), nao filtra nada.
  const workspaces =
    qualifyingBuyerIds.length > 0
      ? allWorkspaces.filter((ws) =>
          qualifyingBuyerIds.includes(ws.buyerOrganizationId)
        )
      : allWorkspaces;

  useEffect(() => {
    if (workspaces.length === 0) return;
    const isAllSelectable = workspaces.length > 1 && workspaceId === ALL_WORKSPACES_ID;
    const exists = isAllSelectable || workspaces.some((workspace) => workspace.id === workspaceId);
    if (!exists) setWorkspaceId(workspaces[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces.map((workspace) => workspace.id).join(",")]);

  useEffect(() => {
    if (workspaceId) {
      localStorage.setItem(INSIGHTS_WORKSPACE_STORAGE_KEY, workspaceId);
    }
  }, [workspaceId]);

  // Restaura o chat salvo do workspace ao trocar (ou reinicia, se nunca visitado).
  // No mount, messages/sentCount/sessionId já vêm inicializados do histórico
  // (useState acima), então esse efeito só deve agir em trocas subsequentes.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!workspaceId) return;
    const saved = chatHistory[workspaceId];
    if (saved) {
      setMessages(saved.messages);
      setSentCount(saved.sentCount);
      setSessionId(saved.sessionId);
    } else {
      setMessages([]);
      setSentCount(0);
      setSessionId(crypto.randomUUID());
    }
  }, [workspaceId]);

  // workspaceId propositalmente fora das deps: no render em que ele muda, ainda
  // carrega messages/sentCount/sessionId do workspace anterior — salvar aqui
  // gravaria o chat errado sob a chave nova. O efeito de restauração acima já
  // dispara um novo render com o estado correto, que então persiste normalmente.
  useEffect(() => {
    if (!workspaceId) return;
    chatHistory[workspaceId] = { messages, sentCount, sessionId };
    sessionStorage.setItem(
      INSIGHTS_CHAT_HISTORY_STORAGE_KEY,
      JSON.stringify(chatHistory)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, sentCount, sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Alguma folha de estilo global do app aplica outline/box-shadow no focus
  // de textarea com especificidade maior que qualquer classe do MUI, mesmo
  // com !important via sx. Isso seta o estilo direto no elemento nativo via
  // CSSStyleDeclaration.setProperty(..., "important"), que ganha de
  // qualquer regra externa, seja ela !important ou não.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    ["outline", "box-shadow", "border"].forEach((prop) => {
      el.style.setProperty(prop, "none", "important");
    });
  }, []);

  const capReached = sentCount >= SESSION_MAX_MESSAGES;
  const isSendActive = Boolean(inputValue.trim()) && !isLoading && !capReached;

  const handleNewConversation = () => {
    setMessages([]);
    setSentCount(0);
    setSessionId(crypto.randomUUID());
  };

  const handleSendMessage = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || isLoading || capReached || !workspaceId) return;

    setMessages((prev) => [...prev, { type: "user", question: trimmed }]);
    setInputValue("");
    setIsLoading(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await sendInsightsAgentMessage(
        {
          content: trimmed,
          workspace_id: workspaceId,
          session_id: sessionId,
        },
        controller.signal
      );

      setMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          content: response.blocks?.length
            ? response.blocks
            : [{ type: "text" as const, text: response.reply }],
        },
      ]);
      setSentCount(response.message_count);
    } catch (error: any) {
      setMessages((prev) => prev.slice(0, -1));

      if (axios.isCancel(error)) return;

      if (error?.response?.data?.message === FEATURE_DISABLED_MESSAGE) {
        setFeatureUnavailable(true);
        return;
      }

      openAlert(getInsightsErrorMessage(error, "Erro ao processar pergunta"), "error");
      if (error?.response?.status === 429) setSentCount(SESSION_MAX_MESSAGES);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancelMessage = () => {
    abortControllerRef.current?.abort();
  };

  const handlePrefabResult = (
    label: string,
    response: IInsightsPrefabResponse
  ) => {
    setMessages((prev) => [
      ...prev,
      {
        type: "assistant",
        content:
          response.rows.length > 0
            ? [{ type: "table" as const, rows: response.rows, label }]
            : [{
                type: "text" as const,
                text: response.message || "Nenhum resultado encontrado.",
                label,
              }],
      },
    ]);
  };

  const handlePrefabError = (message: string) => {
    if (message === FEATURE_DISABLED_MESSAGE) {
      setFeatureUnavailable(true);
      return;
    }
    openAlert(message, "error");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const content = () => {
    if (isCheckingFlag) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 4 }}>
          <CircularProgress size={18} />
        </Box>
      );
    }

    if (!flagEnabled || featureUnavailable) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="info">{FEATURE_DISABLED_MESSAGE}</Alert>
        </Box>
      );
    }

    if (!isLoadingWorkspaces && workspaces.length === 0) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="info">Nenhum workspace disponível para consulta.</Alert>
        </Box>
      );
    }

    return (
      <>
        <ReportButtons
          workspaceId={workspaceId}
          disabled={isLoading || !workspaceId}
          onError={handlePrefabError}
        />
        <ChatContainer>
          <MessagesArea>
          <CenteredColumn sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {messages.length === 0 && !isLoading ? (
              <EmptyStateContainer>
                <Typography
                  variant="h3"
                  fontWeight="600"
                  gutterBottom
                  sx={{
                    fontFamily: INSIGHTS_FONT,
                    color: CONDUCCO_PRIMARY,
                    mb: 4,
                  }}
                >
                  Como posso ajudar?
                </Typography>
                <ChatSuggestions
                  workspaceId={workspaceId}
                  disabled={isLoading}
                  onResult={handlePrefabResult}
                  onError={handlePrefabError}
                />
              </EmptyStateContainer>
            ) : (
              <>
                {messages.map((message, index) =>
                  message.type === "user" ? (
                    <UserMessage key={index} question={message.question} />
                  ) : (
                    <AssistantMessage key={index} content={message.content} />
                  )
                )}
                {isLoading && <LoadingMessage />}
                <div ref={messagesEndRef} />
              </>
            )}
          </CenteredColumn>
        </MessagesArea>

        <InputDock>
          <CenteredColumn>
            {QUICK_QUESTIONS_ENABLED && messages.length > 0 && !capReached && (
              <QuickQuestions onSelectQuestion={handleSendMessage} disabled={isLoading} />
            )}

            {capReached && (
              <Alert
                severity="warning"
                sx={{ mb: 1.5 }}
                action={
                  <Button color="inherit" size="small" onClick={handleNewConversation}>
                    Nova conversa
                  </Button>
                }
              >
                Limite atingido
              </Alert>
            )}

            <InputCard>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Digite sua pergunta..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading || capReached}
                variant="standard"
                inputRef={textareaRef}
                spellCheck={false}
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& textarea, & input": {
                    fontFamily: INSIGHTS_FONT,
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "& textarea:focus, & input:focus": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                }}
              />
              <InputToolbar>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title="Anexar documento ou imagem (em breve)">
                    <IconButton size="small">
                      <AddIcon fontSize="small" sx={{ color: `${CONDUCCO_PRIMARY} !important` }} />
                    </IconButton>
                  </Tooltip>
                  <WorkspaceSelector
                    workspaces={workspaces}
                    value={workspaceId}
                    onChange={setWorkspaceId}
                    disabled={isLoading}
                  />
                </Box>

                <Tooltip
                  title={
                    isLoading
                      ? "Cancelar envio"
                      : `${sentCount}/${SESSION_MAX_MESSAGES} mensagens nesta sessão`
                  }
                >
                  <span>
                    <IconButton
                      onClick={isLoading ? handleCancelMessage : () => handleSendMessage(inputValue)}
                      disabled={isLoading ? false : !inputValue.trim() || capReached}
                      sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        border: `1.5px solid ${CONDUCCO_PRIMARY}`,
                        backgroundColor: isLoading || isSendActive ? CONDUCCO_PRIMARY : "#FFFFFF",
                        transition: "transform 0.15s ease",
                        "&.Mui-disabled": {
                          backgroundColor: "#FFFFFF",
                          borderColor: CONDUCCO_PRIMARY,
                        },
                        "&:hover": {
                          backgroundColor: isLoading || isSendActive ? CONDUCCO_PRIMARY : "#FFFFFF",
                          opacity: 0.92,
                          transform: isLoading || isSendActive ? "scale(1.05)" : "none",
                        },
                      }}
                    >
                      {isLoading ? (
                        <StopIcon fontSize="small" sx={{ color: "#FFFFFF !important" }} />
                      ) : (
                        <ArrowUpwardIcon
                          fontSize="small"
                          sx={{
                            color: isSendActive ? "#FFFFFF !important" : `${CONDUCCO_PRIMARY} !important`,
                          }}
                        />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputToolbar>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: INSIGHTS_FONT }}
                >
                  {sentCount}/{SESSION_MAX_MESSAGES} mensagens
                </Typography>
              </Box>
            </InputCard>
          </CenteredColumn>
        </InputDock>
        </ChatContainer>
      </>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        fontFamily: INSIGHTS_FONT,
      }}
    >
      <Header />
      {content()}
    </Box>
  );
}
